import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle, ArrowDown } from 'lucide-react';

const ContactRow = ({ client, onSave, onBulkApply }) => {
    const [formData, setFormData] = useState({
        pc_fname: client.pc_fname || '',
        pc_pname: client.pc_pname || '',
        pc_email: client.pc_email || '',
        pc_tel: client.pc_tel || '',
        notes: client.notes || ''
    });

    useEffect(() => {
        setFormData({
            pc_fname: client.pc_fname || '',
            pc_pname: client.pc_pname || '',
            pc_email: client.pc_email || '',
            pc_tel: client.pc_tel || '',
            notes: client.notes || ''
        });
    }, [client]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleBlur = (field) => {
        if (formData[field] !== (client[field] || '')) {
            onSave(client.id, { [field]: formData[field] }, false, [field]);
        }
    };

    return (
            <tr className="hover:bg-blue-50/60 group transition-colors odd:bg-white even:bg-blue-50/20">
                <td className="px-4 py-2 text-xs font-bold text-slate-800 border-r border-blue-100">
                {client.name}
            </td>
            {['pc_fname', 'pc_pname', 'pc_email', 'pc_tel', 'notes'].map(field => (
                    <td key={field} className="px-2 py-1 border-r border-blue-100 relative focus-within:bg-blue-50">
                    <div className="flex items-center">
                        {field === 'notes' ? (
                            <textarea
                                name={field}
                                value={formData[field]}
                                onChange={handleChange}
                                onBlur={() => handleBlur(field)}
                                    className="w-full bg-transparent border-none focus:ring-0 text-xs py-1.5 px-2 rounded text-slate-700 placeholder-slate-400 resize-none focus:bg-white"
                                rows={2}
                                placeholder="-"
                            />
                        ) : (
                            <input
                                name={field}
                                value={formData[field]}
                                onChange={handleChange}
                                onBlur={() => handleBlur(field)}
                                    className={`w-full bg-transparent border-none focus:ring-0 text-xs py-1.5 px-2 rounded text-slate-700 placeholder-slate-400 focus:bg-white ${field === 'pc_tel' ? 'font-mono' : ''}`}
                                placeholder={field === 'pc_fname' ? 'Contact Name' : field === 'pc_pname' ? 'Preferred Name' : '-'}
                            />
                        )}
                        <button
                            onMouseDown={(e) => {
                                e.preventDefault();
                                onBulkApply(field, formData[field], client.id);
                            }}
                            className="hidden group-hover:flex items-center justify-center p-1 text-slate-300 hover:text-blue-600 hover:bg-blue-100 rounded mr-1"
                            title={`Apply "${formData[field]}" to all selected clients`}
                        >
                            <ArrowDown className="w-3 h-3" />
                        </button>
                    </div>
                </td>
            ))}
        </tr>
    );
};

const ContactsListModal = ({ selectedClients, onClose, onUpdateClient }) => {
    const clientsToDisplay = Array.from(selectedClients);

    const handleBulkApply = (field, value, sourceClientId) => {
        if (!clientsToDisplay.length) return;
        if (confirm(`Apply "${value}" to ${field} column for all ${clientsToDisplay.length} selected clients?`)) {
            onUpdateClient(sourceClientId, { [field]: value }, true, [field]);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-gradient-to-r from-blue-600 to-indigo-600">
                    <div>
                        <h3 className="text-lg font-bold text-white">Selected Client Contacts ({clientsToDisplay.length})</h3>
                        <p className="text-xs text-blue-100 mt-0.5">Bulk edit with the ↓ icon to apply a value to all selected rows.</p>
                    </div>
                    <button onClick={onClose} className="text-blue-100 hover:text-white hover:bg-white/10 p-1.5 rounded-full transition-colors"><XCircle className="w-6 h-6" /></button>
                </div>
                <div className="overflow-auto flex-1 p-0">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gradient-to-b from-sky-500 to-sky-600 sticky top-0 z-10 shadow-sm">
                            <tr>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60">Company</th>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60">Contact Name</th>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60">Preferred Name</th>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60">Email</th>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60">Phone</th>
                                <th className="px-4 py-3 text-xs font-bold text-white uppercase border-b border-sky-700/60">Notes</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-blue-100">
                            {clientsToDisplay.map(client => (
                                <ContactRow key={client.id} client={client} onSave={onUpdateClient} onBulkApply={handleBulkApply} />
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-2 text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-lg shadow-sm">Done</button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ContactsListModal;